import axios from "axios";
import { store } from "../store";
import config from "../config";
import authHeader from "./auth-header";
import moment from "moment";
import { buildClientListRequest } from "./clientListContract";
import clientphotoApiController from "./clientphoto.service";
import { prepareClientPhotos } from "./prepareClientPhotos";

const API_URL = config.API_URL;

const multipartHeaders = () => {
  const headers = { ...authHeader({ contentType: "multipart/form-data" }) };
  delete headers["Content-Type"];
  return headers;
};

const clientDateOfBirth = (value) => {
  if (!value) return "";
  const parsed = moment(String(value).replace(/\//g, "-"), "DD-MM-YYYY", true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : String(value);
};

const clientMutationPayload = (values = {}) => {
  const payload = {
    date_of_birth: clientDateOfBirth(values.date_of_birth),
    email: values.email || "",
    first_name: values.first_name || "",
    last_name: values.last_name || "",
    phone_number: values.phone_number || "",
    receive_marketing_email:
      values.receive_marketing_email !== undefined
        ? values.receive_marketing_email
        : values.recieve_marketing_email ?? false,
    send_email_notification: values.send_email_notification ?? false,
    send_sms_notification: values.send_sms_notification ?? false,
  };

  // The Flask mutation DTO keeps the optional profile fields that the
  // Laravel client form already collects.  Omit undefined values so the
  // required transport contract remains stable for older callers.
  ["country_code", "address", "street", "suburb", "state", "postcode", "description"].forEach((field) => {
    if (values[field] !== undefined) payload[field] = values[field] || "";
  });
  if (values.gender !== undefined) {
    payload.gender = values.gender && typeof values.gender === "object" ? values.gender.value : values.gender || "";
  }
  return payload;
};

const pendingClientDeleteKeys = new Map();

const newClientDeleteKey = (values = {}) => {
  const supplied = values && values.idempotency_key;
  if (typeof supplied === "string" && supplied.trim()) return supplied.trim();
  const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `client-delete-${values.id || "new"}-${Date.now()}-${randomPart}`;
};

const acquireClientDeleteKey = (values = {}) => {
  const target = `${values.operation === "delete" ? "delete" : "archive"}:${values.id || "new"}`;
  const supplied = typeof values.idempotency_key === "string"
    ? values.idempotency_key.trim()
    : "";
  let pending = pendingClientDeleteKeys.get(target);
  if (!pending || (supplied && supplied !== pending.key)) {
    pending = { key: newClientDeleteKey(values), inFlight: 0 };
    pendingClientDeleteKeys.set(target, pending);
  }
  pending.inFlight += 1;
  return { pending, target };
};

const create = async (values = {}) => {
  let selectedPhoto;
  if (values.profile_photo) {
    try {
      [selectedPhoto] = await prepareClientPhotos([values.profile_photo]);
    } catch (error) {
      throw { response: { status: 422, data: { message: error.message } } };
    }
  }
  const action = "afterlogin/client/store";
  const response = await axios.post(API_URL + action, clientMutationPayload(values), { headers: authHeader() });
  if (!selectedPhoto) return response;
  // Client creation has committed. Photo failure must not turn this into a
  // rejected create that encourages a second client mutation on retry.
  try {
    const clientId = Number(response.data?.id);
    if (!Number.isSafeInteger(clientId) || clientId <= 0) throw new Error("Missing created client identity");
    const uploaded = await clientphotoApiController.create({ client_id: clientId, myFiles: [selectedPhoto] });
    const photo = Array.isArray(uploaded.data) && uploaded.data.length === 1 ? uploaded.data[0] : null;
    const photoId = Number(photo?.id);
    if (!Number.isSafeInteger(photoId) || photoId <= 0 || Number(photo?.client_id) !== clientId) {
      throw new Error("Photo ownership was not confirmed");
    }
    await clientphotoApiController.update({ id: photoId, client_id: clientId });
    return response;
  } catch (_error) {
    return { ...response, data: { ...response.data,
      profile_photo_warning: "Client created. The profile photo could not be saved. Open this client's Photos to upload or choose the profile image; do not create the client again.",
    } };
  }
};

const update = (values) => {
  const action = "afterlogin/client/update/" + values.id;
  return axios.post(API_URL + action, clientMutationPayload(values), { headers: authHeader() });
};

const childStore = (values = {}) => {
  const child = { ...clientMutationPayload(values) };
  delete child.email;
  delete child.phone_number;
  delete child.receive_marketing_email;
  return axios.post(API_URL + "afterlogin/client/child/store", {
    ...child,
    parent_client_id: values.parent_client_id,
  }, { headers: authHeader() });
};

const parentLink = ({ parent_client_id, child_client_id } = {}) => axios.post(
  API_URL + "afterlogin/client/parent/link",
  { parent_client_id, child_client_id },
  { headers: authHeader() },
);

const parentCandidates = ({ child_client_id } = {}) => axios.post(
  API_URL + "afterlogin/client/parent/candidates",
  { child_client_id },
  { headers: authHeader() },
);

const parentUnlink = ({ child_client_id } = {}) => axios.post(
  API_URL + "afterlogin/client/parent/unlink",
  { child_client_id },
  { headers: authHeader() },
);

// Grid and list use the same endpoint and payload. Share only concurrent
// list reads; settled responses must never hide subsequent client edits.
const pendingClientListReads = new Map();

const view = (values) => {
  const request = buildClientListRequest(values);
  const headers = authHeader();
  if (request.body.id) {
    return axios.post(API_URL + request.url, request.body, { headers });
  }
  const key = JSON.stringify([request.url, request.body, headers]);
  const pending = pendingClientListReads.get(key);
  if (pending) return pending;

  const shared = Promise.resolve(axios.post(API_URL + request.url, request.body, { headers }))
    .finally(() => {
      if (pendingClientListReads.get(key) === shared) pendingClientListReads.delete(key);
    });
  pendingClientListReads.set(key, shared);
  return shared;
};

const archived = (values = {}) => {
  const params = new URLSearchParams({page: String(values.page || 1), limit: String(values.limit || 20)});
  if (values.q) params.set("q", values.q);
  return axios.post(API_URL + `afterlogin/client/archived?${params}`, {}, {headers: authHeader()});
};
const archivePreview = values => axios.post(API_URL + `afterlogin/client/archive-preview/${values.id}`, {}, {headers: authHeader()});
const unarchive = (values) => axios.post(API_URL + `afterlogin/client/unarchive/${values.id}`, {}, {headers: authHeader()});

const deleted = (values) => {
  const auth_key = store.getState()?.auth?.user?.auth_key;
  const action = values.operation === "delete"
    ? `afterlogin/client/delete/${values.id}`
    : `afterlogin/client/archive/${values.id}`;
  const { pending, target } = acquireClientDeleteKey(values);
  const data = {
    auth_key: auth_key,
    action: action,
    ...(values.confirm_commitments === true ? {confirm_commitments:true,review_token:values.review_token} : {}),
  };
  let request;
  try {
    request = axios.post(API_URL + action, data, {
      headers: { ...authHeader(), "Idempotency-Key": pending.key },
    });
  } catch (error) {
    pending.inFlight -= 1;
    throw error;
  }
  return Promise.resolve(request).then(
    (response) => {
      pending.inFlight -= 1;
      if (pending.inFlight === 0 && pendingClientDeleteKeys.get(target) === pending) {
        pendingClientDeleteKeys.delete(target);
      }
      return response;
    },
    (error) => {
      pending.inFlight -= 1;
      throw error;
    },
  );
};

const mergeList = (values) => {
  const action = `afterlogin/client/mergeList`;
  const data = {
    exclude_id: values && values.exclude_id ? values.exclude_id : "",
    page: values && values.page ? values.page : 1,
    per_page: values && values.per_page ? values.per_page : 10,
    q: values && values.q ? values.q : "",
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const mergeClients = (values) => {
  const action = `afterlogin/client/merge`;
  const data = {
    main_client_id: values.main_client_id,
    merge_client_ids: values.merge_client_ids,
    ...(values.action ? {action: values.action} : {}),
    ...(values.resolution ? {resolution: values.resolution} : {}),
  };
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const suggetionlist = (values = {}) => {
  const request = buildClientListRequest(values);
  const data = {
    ...request.body,
    id: values.id || "",
    suggetion_match: values.suggetion_match || "",
  };
  return axios.post(API_URL + request.url, data, { headers: authHeader() });
};

const clientimport = (values) => {
  const action = `afterlogin/client/clientimport`;
  const formData = new FormData();
  for (let value in values || {}) {
    formData.append(value, values[value]);
  }
  return axios.post(API_URL + action, formData, { headers: multipartHeaders() });
};

const clientexport = (values) => {
  const action = `afterlogin/client/clientexport`;
  const payload = {};
  if (values && values.fields !== undefined) payload.fields = values.fields;
  if (values && values.sort !== undefined) payload.sort = values.sort;
  if (values && values.id !== undefined) payload.id = values.id;
  return axios.post(API_URL + action, payload, { headers: authHeader() });
};

const downloadClientExport = (handle) => axios.get(
  `${API_URL}afterlogin/client/clientexport/download/${encodeURIComponent(handle)}`,
  { headers: authHeader(), responseType: "blob" },
);

const clientmailchimpsync = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const action = `afterlogin/client/clientmailchimp`;
  const formData = new FormData();
  for (let value in values) {
    formData.append(value, values[value]);
  }
  formData.append("auth_key", auth_key);
  formData.append("action", action);
  return axios.post(API_URL + action, formData, { headers: authHeader() });
};

const clientvoucherimport = (values) => {
  const action = `afterlogin/client/clientimportvoucher`;
  const formData = new FormData();
  for (let value in values || {}) {
    formData.append(value, values[value]);
  }
  return axios.post(API_URL + action, formData, { headers: multipartHeaders() });
};

const clientvouchersample = (values) => {
  const action = `common/sampleclientvoucherexport`;
  return axios.post(API_URL + action, values || {}, { headers: authHeader(), responseType: "blob" });
};

const clientvoucherexports = (values) => {
  const action = `common/sampleclientvoucherexport`;
  return axios.post(API_URL + action, values || {}, { headers: authHeader(), responseType: "blob" });
};

const clientApiController = {
  archived,
  archivePreview,
  unarchive,
  create,
  childStore,
  parentLink,
  parentCandidates,
  parentUnlink,
  view,
  mergeList,
  mergeClients,
  update,
  deleted,
  suggetionlist,
  clientimport,
  clientexport,
  downloadClientExport,
  clientmailchimpsync,
  clientvoucherimport,
  clientvouchersample,
  clientvoucherexports
};
export default clientApiController;
